import { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/queryClient';
import { Layout } from './components/Layout/Layout';
import { TrackView } from './components/Track/TrackView';
import { BacklogView } from './components/Backlog/BacklogView';
import { SummaryView } from './components/Summary/SummaryView';
import { ArchiveView } from './components/Archive/ArchiveView';
import { CalendarView } from './components/Calendar/CalendarView';
import { RecurringView } from './components/Recurring/RecurringView';
import { PlannerView } from './components/Planner/PlannerView';
import { MeetingCanvas } from './components/Calendar/MeetingCanvas';
import { SettingsPanel } from './components/Settings/SettingsPanel';
import { DetailPanel } from './components/shared/DetailPanel';
import { CommandPalette } from './components/shared/CommandPalette';
import { ContextMenu } from './components/shared/ContextMenu';
import { AuthGuard } from './components/Auth/AuthGuard';
import { BillingBanner } from './components/Billing/BillingBanner';
import { TutorialController } from './components/Tutorial';
import { useTutorialStore } from './store/tutorialStore';
import { LoginPage } from './components/Auth/LoginPage';
import { SignupPage } from './components/Auth/SignupPage';
import { ForgotPasswordPage } from './components/Auth/ForgotPasswordPage';
import { PrivacyPolicy } from './components/Legal/PrivacyPolicy';
import { TermsOfService } from './components/Legal/TermsOfService';
import { useAppStore } from './store';
import { RateLimitMonitor } from './components/Dev/RateLimitMonitor';
import { useTaskById } from './hooks/useTasks';
import { useRecurrenceGenerator } from './hooks/useRecurrence';
import { useContextMenu } from './hooks/useContextMenu';
import { useCalendarEvents } from './hooks/useCalendarEvents';
import { startTimelineBlockChecker, stopTimelineBlockChecker, setTimelineBlockCallback } from './services/timelineBlockChecker';
import { playChime } from './services/chime';
import { ToastProvider, useToast } from './components/Toast/ToastProvider';
import { api } from './api/client';
import { Sentry } from './lib/sentry';
import { useIsFirstTimeUser } from './hooks/useIsFirstTimeUser';
import { useSpaces } from './hooks/useSpaces';

function TutorialAutoStart() {
  const { isFirstTime, isLoading } = useIsFirstTimeUser();
  const { isCompleted, isActive, startTutorial } = useTutorialStore();

  useEffect(() => {
    if (isLoading || isCompleted || isActive || !isFirstTime) return;
    if (window.innerWidth < 768) return;
    const timer = setTimeout(() => startTutorial(), 1500);
    return () => clearTimeout(timer);
  }, [isLoading, isCompleted, isActive, isFirstTime, startTutorial]);

  return null;
}

function AppContent() {
  const { addToast } = useToast();
  const [authView, setAuthView] = useState<"login" | "signup" | "forgot">("login");

  useEffect(() => {
    setTimelineBlockCallback((block) => {
      addToast(`${block.title} starts at ${block.scheduledStartTime}`, 'timeline');
      playChime();
    });
    startTimelineBlockChecker();
    return () => { stopTimelineBlockChecker(); };
  }, [addToast]);
  const currentView = useAppStore((s) => s.currentView);
  const selectedTaskId = useAppStore((s) => s.selectedTaskId);
  const setSelectedTaskId = useAppStore((s) => s.setSelectedTaskId);
  const currentSpaceId = useAppStore((s) => s.currentSpaceId);
  useSpaces(); // Load spaces on startup
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const [showSettings, setShowSettings] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const ctxMenu = useContextMenu();
  const selectedMeetingId = useAppStore((s) => s.selectedMeetingId);
  const { data: calendarEvents = [] } = useCalendarEvents();
  const selectedMeeting = calendarEvents.find((e) => e.id === selectedMeetingId) ?? null;
  const selectedTask = useTaskById(selectedTaskId);
  const { regenerate } = useRecurrenceGenerator();

  const rolloverYesterday = useCallback(async (spaceId: string | null) => {
    if (!spaceId) return;
    await api.tasks.rollover(spaceId);
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
  }, []);

  // On startup: rollover yesterday's incomplete tasks to today
  useEffect(() => {
    rolloverYesterday(currentSpaceId);
  }, [currentSpaceId, rolloverYesterday]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowPalette(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Midnight watcher: detect when the real calendar date changes, then advance
  // selectedDate and re-run generation + rollover. Tracks lastKnownDate locally
  // so navigating to a past date doesn't trigger this.
  useEffect(() => {
    let lastKnownDate = format(new Date(), 'yyyy-MM-dd');
    const intervalId = setInterval(async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      if (today !== lastKnownDate) {
        lastKnownDate = today;
        setSelectedDate(today);
        await regenerate();
        rolloverYesterday(currentSpaceId);
      }
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [currentSpaceId, setSelectedDate, rolloverYesterday, regenerate]);

  const authFallback =
    authView === "forgot" ? (
      <ForgotPasswordPage onBack={() => setAuthView("login")} />
    ) : authView === "signup" ? (
      <SignupPage onSwitchToLogin={() => setAuthView("login")} />
    ) : (
      <LoginPage
        onSwitchToSignup={() => setAuthView("signup")}
        onForgotPassword={() => setAuthView("forgot")}
      />
    );

  return (
    <AuthGuard fallback={authFallback}>
      <BillingBanner />
      <TutorialAutoStart />
      <TutorialController />
      <Layout onOpenSettings={() => setShowSettings(true)} onOpenPalette={() => setShowPalette(true)}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="h-full"
          >
            {currentView === 'track' && <TrackView />}
            {currentView === 'planner' && <PlannerView />}
            {currentView === 'backlog' && <BacklogView />}
            {currentView === 'summary' && <SummaryView />}
            {currentView === 'archive' && <ArchiveView />}
            {currentView === 'calendar' && (
              selectedMeeting ? <MeetingCanvas event={selectedMeeting} /> : <CalendarView />
            )}
            {currentView === 'recurring' && <RecurringView />}
          </motion.div>
        </AnimatePresence>
        {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
        {showPalette && <CommandPalette onClose={() => setShowPalette(false)} />}
        <AnimatePresence>
          {selectedTask && (
            <DetailPanel task={selectedTask} onClose={() => setSelectedTaskId(null)} />
          )}
        </AnimatePresence>
        {ctxMenu.isOpen && (
          <ContextMenu x={ctxMenu.x} y={ctxMenu.y} actions={ctxMenu.actions} onClose={ctxMenu.close} />
        )}
      </Layout>
    </AuthGuard>
  );
}

function App() {
  // Legal pages are served before auth guard, no login required
  const path = window.location.pathname;
  if (path === "/privacy") return <PrivacyPolicy />;
  if (path === "/terms") return <TermsOfService />;

  return (
    <Sentry.ErrorBoundary fallback={
      <div style={{ padding: "2rem", color: "var(--color-text-primary, #fff)" }}>
        Something went wrong. We've been notified.
      </div>
    }>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AppContent />
          {import.meta.env.VITE_DEV_TOOLS === '1' && <RateLimitMonitor />}
        </ToastProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
